import axios from 'axios';
import jwtDecode from 'jwt-decode';
import EventEmitter from './EventEmitter';
import { apiBaseAddress } from 'modules/api/index';
import { history } from 'dsilo-ui-components';
import { Request } from "../modules/api/clientHelper";
import { acquireAadTokenSilent, logoutRedirect } from '../Pages/Login/msalService';
import { deleteCookie, getCookie, setCookie } from '../components/CookieConsent/utils';
import { PUBLIC_PATH } from 'constants/index'
import { store } from '../store/index';
import { ssoExpire } from '../store/actions';

const BASE_URL = process.env.REACT_APP_API_BASE_URL;
const BASE_URL_CREATE = process.env.REACT_APP_API_BASE_URL_CREATE;
let timeout;

// Debug logger - enable via localStorage.setItem('debugSSO', 'true')
const debugLog = (...args) => {
	const isDebug = localStorage.getItem('debugSSO') === 'true'
	if (isDebug) console.log('[jwtService]', ...args)
}

class jwtService extends EventEmitter {
	constructor() {
		super();
		this.timeout = null; // Instance-level timeout instead of module-level
		this.ssoTimeoutId = null; // For setTimeout in setSession
	}

	init() {
		this.setInterceptors();
		this.handleAuthentication();
	}

	setInterceptors = () => {
		axios.interceptors.response.use(
			response => {
				return response;
			},
			err => {
				return new Promise((resolve, reject) => {
					if (err.response?.status === 401 && err.config && !err.config.__isRetryRequest) {
						// Check if user is logged in via SSO - don't clear session, let SilentSSO handle it
						const isSsoLogin = localStorage.getItem('isSsoLogin');
						if (isSsoLogin) {
							// For SSO users, dispatch ssoExpire to trigger SilentSSO refresh
							console.log('401 received for SSO user - triggering SilentSSO refresh');
							store.dispatch(ssoExpire());
						} else {
							// For non-SSO users, logout
							this.emit('onAutoLogout', 'Invalid access_token');
							this.setSession(null);
							if (this.timeout) {
								clearInterval(this.timeout);
								this.timeout = null;
							}
						}
					}
					throw err;
				});
			},
		);
	};

	handleAuthentication = () => {
		debugLog("=== handleAuthentication ===");
		let access_token = this.getAccessToken();
		debugLog("access_token exists:", !!access_token);
		if (!access_token) {
			const isAdLogin = localStorage.getItem('isAdLogin') || '';
			const isSsoLogin = localStorage.getItem('isSsoLogin') || '';
			let ssoConfig = null;
			let userEmailReport = localStorage.getItem('userEmailReport')
			if (userEmailReport) {
				let emailCheckData = JSON.parse(window.atob(userEmailReport))
				ssoConfig = emailCheckData?.ssoConfig
			}
			debugLog("No token - checking SSO config:");
			debugLog("  isAdLogin:", isAdLogin);
			debugLog("  isSsoLogin:", isSsoLogin);
			debugLog("  ssoConfig exists:", !!ssoConfig);
			const isMFARoute = location.pathname === "/mfaaad"; // exact match after domain
			debugLog("  isMFARoute:", isMFARoute);
			if (!isMFARoute && !ssoConfig && isAdLogin !== 'true' && (isSsoLogin !== 'true')) {
				debugLog("→ No SSO/AD config, emitting onAutoLogout");
				this.setSession(null);
				if (this.timeout) {
					clearInterval(this.timeout);
					this.timeout = null;
				}
				this.emit('onAutoLogout', 'access_token expired');
			} else {
				debugLog("→ SSO/AD config found, NOT logging out");
			}
			return;
		}

		if (this.isAuthTokenValid(access_token)) {
			debugLog("→ Token valid, setting session and emitting onAutoLogin");
			this.setSession(access_token);
			this.emit('onAutoLogin', true);
		} else {
			debugLog("→ Token invalid/expired, emitting onAutoLogout");
			this.setSession(null);
			if (this.timeout) {
				clearInterval(this.timeout);
				this.timeout = null;
			}
			this.emit('onAutoLogout', 'access_token expired');
		}
	};

	createUser = data => {
		return new Promise((resolve, reject) => {
			Request(BASE_URL + '/user/register', {
				method: 'POST',
				payload: {
					...data
				}
			})
				.then(response => {
					console.log('respon', response);
					const { data } = response.data;
					resolve(data);
				})
				.catch(err => {
					console.error('err ==> ', err);
					reject(err.response.message);
				});
		});
	};

	verifyEmail = data => {
		const { token } = data;
		return Request(BASE_URL + '/user/verifyEmail', {
			method: 'GET',
			headers: {
				Authorization: `${token}`,
				'content-type': 'application/json'
			},
		})
	};

	checkEmailForAad = (payload) => {
		return new Promise((resolve, reject) => {
			Request(BASE_URL + '/user/emailcheck', {
				method: 'POST',
				payload
			}).then(response => {
				const { data } = response;
				resolve({ data: data });
			}).catch(err => {
				console.error('err ==> ', err);
				reject(err?.response);
			});
		});
	};

	checkEmailForLLSE = (payload) => {
		debugLog("=== checkEmailForLLSE ===");
		debugLog("payload:", payload);
		const endpoint = `/user/emailcheck/${payload.sd === true ? "sd" : "llse"}`;
		debugLog("endpoint:", endpoint);
		return new Promise((resolve, reject) => {
			Request(BASE_URL + endpoint, {
				method: 'POST',
				payload
			}).then(response => {
				const { data } = response;
				debugLog("✓ checkEmailForLLSE response:", data);
				resolve({ data: data });
			}).catch(err => {
				debugLog("✗ checkEmailForLLSE error:", err);
				reject(err?.response);
			});
		});
	};
	
	aadLogin = (data) => {
		return new Promise((resolve, reject) => {
			const { orgId, accessToken } = data;
			Request(BASE_URL + '/aadlogin', {
				method: 'GET',
				headers: {
					Authorization: `${accessToken}`,
					orgId,
					'content-type': 'application/json'
				},
			}).then(response => {
				const { data } = response;
				// if (data?.refreshToken) {
				// 	timeout = setInterval(() => {
				// 		this.refreshToken();
				// 	}, 9 * 60000);
				// }
				resolve({ data: data });
			}).catch(err => {
				console.error('err ==> ', err);
				reject(err?.response);
			});
		});
	};

	// This is used to send the SAMLResponse to platform API to validate and sent the token details.
	validateSAMLResponse = (samlData) => {
		debugLog("=== validateSAMLResponse ===");
		debugLog("samlData length:", samlData?.length);
		return new Promise((resolve, reject) => {
			Request(BASE_URL + '/saml/acs/internal', {
				method: 'POST',
				headers: {
					'content-type': 'application/json'
				},
				payload: {
					SAMLResponse: samlData
				}
			}).then(response => {
				const { data } = response;
				debugLog("✓ validateSAMLResponse success:", data);
				resolve({ data: data });
			}).catch(err => {
				debugLog("✗ validateSAMLResponse error:", err);
				reject(err?.response);
			});
		});
	};

	signInWithEmailAndPassword = ({ userconfig, tnc }) => {
		return new Promise((resolve, reject) => {
			Request(BASE_URL + '/user/login', {
				method: 'POST',
				payload: {
					userconfig,
					enc: true,
					tnc
				}
			})
				.then(response => {
					console.log("response ====", response);
					const { data } = response;
					this.setSession(data.token);
					// localStorage.setItem('refreshToken',data.refreshToken)
					localStorage.setItem('userId', data?._id);
					// this would reset token expiry time
					if (this.timeout) {
						clearInterval(this.timeout);
					}
					this.timeout = setInterval(() => {
						this.refreshToken();
					}, 9 * 60000);
					resolve({ data: data, setSession: this.setSession });
				})
				.catch(err => {
					console.error('err ==> ', err);
					reject(err?.response);
				});
		});
	};

	refreshToken = () => {
		console.log("document.visibilityState =====", document.visibilityState)
		if (document.visibilityState === "visible") {
			console.log("initiate refresh token")
			return new Promise((resolve, reject) => {
				let token = localStorage.getItem('jwt_access_token')
				if (token) {
					Request(BASE_URL + `/user/refreshToken`, {
						method: 'POST',
						payload: {},
						headers: {
							Authorization: `${token}`,
						}
					})
						.then(response => {
							const { data } = response;
							this.setSession(data.token);
							resolve(response.data);
						})
						.catch(err => {
							console.error('err ==> ', err);
							this.setSession(null);
							if (this.timeout) {
								clearInterval(this.timeout);
								this.timeout = null;
							}
							history.push({
								pathname: `/${PUBLIC_PATH}login`
							});
							reject(err.response);
						});
				} else {
					console.log("avoiding refresh token - missing token")
				}
			});
		} else {
			console.log("avoiding refresh token")
			return;
		}
	};

	refreshAadToken = async (forceRefresh = false) => {
		const isAdLogin = localStorage.getItem('isAdLogin') === 'true';
		const rawMsalAdConfig = localStorage.getItem('adConfig');
		if (!isAdLogin || !rawMsalAdConfig) {
			return null;
		}

		// Avoid noisy refresh attempts while tab is hidden.
		if (document.visibilityState !== 'visible') {
			return null;
		}

		try {
			const msalAdConfig = JSON.parse(window.atob(rawMsalAdConfig));
			const result = await acquireAadTokenSilent(msalAdConfig, {
				forceRefresh,
				allowRedirectFallback: true,
			});
			const accessToken = result?.accessToken || '';
			if (accessToken) {
				this.setSession(accessToken);
			}
			return accessToken;
		} catch (error) {
			console.error('AAD token refresh failed:', error);
			return null;
		}
	};

	startSessionRefreshTimer = () => {
		// Reset existing timer so this is safe to call repeatedly.
		if (this.timeout) {
			clearInterval(this.timeout);
			this.timeout = null;
		}

		const isAdLogin = localStorage.getItem('isAdLogin') || '';
		const isSsoLogin = localStorage.getItem('isSsoLogin') || '';
		let ssoConfig = null;
		const userEmailReport = localStorage.getItem('userEmailReport');
		if (userEmailReport) {
			const emailCheckData = JSON.parse(window.atob(userEmailReport));
			ssoConfig = emailCheckData?.ssoConfig;
		}

		// Keep MFA/AD tokens fresh before the ~10 min expiry.
		if (isAdLogin === 'true') {
			this.timeout = setInterval(() => {
				this.refreshAadToken();
			}, 9 * 60000);
			this.refreshAadToken();
			return;
		}

		// Existing refresh-token behavior for non-AD flows.
		if (isSsoLogin !== 'true' || (isSsoLogin == 'true' && ssoConfig?.system === 'pingID')) {
			this.timeout = setInterval(() => {
				this.refreshToken();
			}, 9 * 60000);
			this.refreshToken();
		}
	};

	resetPassword = data => {
		return new Promise((resolve, reject) => {
			Request(BASE_URL + `/user/resetpwd`, {
				method: 'POST',
				payload: {
					...data,
				},
				headers: {
					Authorization: `${data.token}`,
				}
			})
				.then(response => {
					resolve(response.data);
				})
				.catch(err => {
					console.error('err ==> ', err);
					reject(err.response);
				});
		});
	};

	changePassword = data => {
		return new Promise((resolve, reject) => {
			Request(BASE_URL + `/user/changepwd`, {
				method: 'POST',
				payload: data.payload,
				headers: {
					Authorization: `${data.token}`,
				}
			})
				.then(response => {
					resolve(response);
				})
				.catch(err => {
					console.error('err ==> ', err);
					reject(err.response);
				});
		});
	};

	verifyUser = data => {
		console.log("data ===", data);
		const { token } = data;
		return Request(BASE_URL + `/user/verifyuser`, {
			headers: {
				Authorization: `${token}`,
				'content-type': 'application/json'
			},
			method: 'GET',
		})
	};

	signInWithToken = () => {
		console.log("signInWithToken ==")
		return new Promise((resolve, reject) => {
			let accessToken = this.getAccessToken();
			this.setSession(accessToken);
			if (this.isAuthTokenValid(accessToken)) {
				Request(BASE_URL + `/user/userdetails`, {
					method: 'GET',
				})
					.then(response => {
						const userDetails = response?.data;
						if (userDetails) {
							let user = {
								isAuthenticated: true,
								id: userDetails._id,
								userName: userDetails.userName,
								roles: userDetails.roles,
								orgId: userDetails.orgId,
								email: userDetails.email,
								subscription: userDetails.subscription,
								...userDetails,
							};
							localStorage.setItem('userId', userDetails?._id);
							// Restart session refresh loop on bootstrap/hard refresh.
							this.startSessionRefreshTimer();
							resolve(user);
						} else {
							reject(response?.data);
						}
					})
					.catch(err => {
						console.error('err ==> ', err);
						reject(err.response);
					});
			} else {
				reject('error');
			}
		});
	};

	updateUserData = user => {
		return Request(apiBaseAddress() + '/api/auth/user/update', {
			method: 'POST',
			payload: {
				user: user,
			}
		});
	};

	setSession = (access_token, type = undefined) => {
		debugLog("=== setSession ===");
		debugLog("access_token:", access_token ? "exists" : "null");
		debugLog("type:", type);
		if (type === "mfa") {
			deleteCookie('tnc');
			debugLog("→ MFA type, no action");
		} else if (type === "sso") {
			debugLog("→ SSO type, setting timeout to remove isSsoRedirectSuccess");
			// Clear existing timeout if any
			if (this.ssoTimeoutId) {
				clearTimeout(this.ssoTimeoutId);
			}
			this.ssoTimeoutId = setTimeout(() => {
				localStorage.removeItem('isSsoRedirectSuccess');
				this.ssoTimeoutId = null;
			}, 2000);
		} else {
			if (access_token) {
				debugLog("→ Setting jwt_access_token in localStorage");
				localStorage.setItem('jwt_access_token', access_token);
				axios.defaults.headers.common['Authorization'] = access_token;
			} else {
				debugLog("→ Clearing session (removing tokens from localStorage)");
				const userId = localStorage.getItem('userId');
				localStorage.removeItem('jwt_access_token');
				localStorage.removeItem('jwtuserData');
				localStorage.removeItem('userEmailResponse');			
				localStorage.removeItem('userSession');
				localStorage.removeItem('refreshToken')
				localStorage.removeItem('ssoRedirect');
				localStorage.removeItem(`${userId}`);
				localStorage.removeItem('userId');
				localStorage.removeItem('isSsoRedirectSuccess');
				delete axios.defaults.headers.common['Authorization'];
				// deleteCookie('tnc'); // here it is getting removed when redirected to MFA so commented this line
			}
		}
	};

	removeAdSsoConfig = () => {
		debugLog("=== removeAdSsoConfig ===");
		debugLog("Removing AD and SSO config from localStorage");
		// AAD
		localStorage.removeItem('isAdLogin');
		localStorage.removeItem('adConfig');
		localStorage.removeItem('mfaLoginHint');
		// SSO
		localStorage.removeItem('isSsoLogin');
		localStorage.removeItem('ssoConfig');
		localStorage.removeItem('ssoRedirect');
		localStorage.removeItem('isSsoRedirectSuccess');
		debugLog("✓ AD/SSO config removed");
	}

	logout = async () => {
		let token = localStorage.getItem('jwt_access_token');
		const isAdLogin = localStorage.getItem('isAdLogin') || '';
		if (token) {
			if (isAdLogin === 'true') {
				const rawMsalAdConfig = localStorage.getItem('adConfig');
				if (rawMsalAdConfig) {
					const msalAdConfig = JSON.parse(window.atob(rawMsalAdConfig));
					console.log('from session storage ::: *** ', msalAdConfig)
					this.setSession(null);
					this.removeAdSsoConfig();
					const logoutResponse = await logoutRedirect(msalAdConfig);
					console.log('result ::: *** ', logoutResponse);
				} else {
					// TODO: For SSO we need to implement the logout functinality.
					this.setSession(null);
					this.removeAdSsoConfig();
					history.push({
						pathname: `/${PUBLIC_PATH}login`
					});
				}
			} else {
				return new Promise((resolve, reject) => {
					Request(BASE_URL + `/user/logout`, {
						method: 'POST',
						payload: {},
					})
						.then(response => {
							console.log('response ===????', response);
							this.setSession(null);
							this.removeAdSsoConfig();
							if (this.timeout) {
								clearInterval(this.timeout);
								this.timeout = null;
							}
							history.push({
								pathname: `/${PUBLIC_PATH}login`
							});
							if (response.data) {
								resolve(response.data);
							} else {
								reject(response.data.message);
							}
						})
						.catch(err => {
							console.log('err ===????', err);
							this.setSession(null);
							if (this.timeout) {
								clearInterval(this.timeout);
								this.timeout = null;
							}
							reject(err.response);
						});
				});
			}
		} else {
			history.push({
				pathname: `/${PUBLIC_PATH}login`
			});
		}
	};

	isAuthTokenValid = access_token => {
		if (!access_token) {
			return false;
		}
		try {
			const decoded = jwtDecode(access_token);
			const currentTime = Date.now() / 1000;
			if (decoded.exp < currentTime) {
				console.warn('access token expired');
				return false;
			} else {
				return true;
			}
		} catch (e) {
			return false;
		}

	};

	getRefreshToken = () => {
		return window.localStorage.getItem('refreshToken');
	};

	getAccessToken = () => {
		return window.localStorage.getItem('jwt_access_token');
	};

	// Cleanup method to clear all timers
	cleanup() {
		if (this.timeout) {
			clearInterval(this.timeout);
			this.timeout = null;
		}
		if (this.ssoTimeoutId) {
			clearTimeout(this.ssoTimeoutId);
			this.ssoTimeoutId = null;
		}
	}
}

const instance = new jwtService();

export default instance;
